// src/features/time_series/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import {
  DatePickerField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

import { categoryOptions, windowOptions } from "./filter";

const client = "instit-quantilia";
const table_name = "time_series";

const normalizeEnumToString = (val: unknown) =>
  val === null || val === undefined || val === "" ? val : String(val);

const normalizeJsonFieldToString = (val: unknown) => {
  if (val === null || val === undefined) return val;
  if (typeof val === "string") return val;
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

// Zod schema for the form
const FormSchema = z.object({
  owner_id: z.coerce.number().optional().nullable(),

  owner_type: z.string().nullable().optional(),

  // Optional select
  category: z.preprocess(
    normalizeEnumToString,
    z.string().nullable().optional(),
  ),

  // Optional select
  window: z.preprocess(normalizeEnumToString, z.string().nullable().optional()),

  // Number field
  current_value: z.coerce.number().optional().nullable(),

  // Dates
  full_start_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  current_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  start_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  end_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  calculation_start_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),

  // JSON fields as plain text
  perm_values: z.preprocess(
    normalizeJsonFieldToString,
    z.string().nullable().optional(),
  ),
  tmp_values: z.preprocess(
    normalizeJsonFieldToString,
    z.string().nullable().optional(),
  ),

  source: z.string().nullable().optional(),
});

export function TimeSeriesNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t("time_series.title", {
                  ns: "tables",
                  defaultValue: "Time Series",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Owner ID & owner_type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="owner_id"
                    label={t(`${table_name}.columns.owner_id`, {
                      ns: "tables",
                      defaultValue: "Owner ID",
                    })}
                    type="number"
                    control={form.control}
                    allowZero={false}
                    allowNegative={false}
                  />
                  <InputField
                    name="owner_type"
                    label={t(`${table_name}.columns.owner_type`, {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2: category & window */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="category"
                    label={t(`${table_name}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Category",
                    })}
                    options={categoryOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="window"
                    label={t(`${table_name}.columns.window`, {
                      ns: "tables",
                      defaultValue: "Window",
                    })}
                    options={windowOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 3: current_value & full_start_date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="current_value"
                    label={t(`${table_name}.columns.current_value`, {
                      ns: "tables",
                      defaultValue: "Current Value",
                    })}
                    type="number"
                    control={form.control}
                    allowZero
                    allowNegative
                  />
                  <DatePickerField
                    name="full_start_date"
                    label={t(`${table_name}.columns.full_start_date`, {
                      ns: "tables",
                      defaultValue: "Full Start Date",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 4: perm_values & tmp_values (JSON) */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="perm_values"
                    label={t(`${table_name}.columns.perm_values`, {
                      ns: "tables",
                      defaultValue: "Perm Values (JSON)",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.json")}
                  />
                  <TextareaField
                    name="tmp_values"
                    label={t(`${table_name}.columns.tmp_values`, {
                      ns: "tables",
                      defaultValue: "Tmp Values (JSON)",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.json")}
                  />
                </div>

                {/* Row 5: current_date & start_date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="current_date"
                    label={t(`${table_name}.columns.current_date`, {
                      ns: "tables",
                      defaultValue: "Current Date",
                    })}
                    control={form.control}
                  />
                  <DatePickerField
                    name="start_date"
                    label={t(`${table_name}.columns.start_date`, {
                      ns: "tables",
                      defaultValue: "Start Date",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 6: end_date & calculation_start_date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="end_date"
                    label={t(`${table_name}.columns.end_date`, {
                      ns: "tables",
                      defaultValue: "End Date",
                    })}
                    control={form.control}
                  />
                  <DatePickerField
                    name="calculation_start_date"
                    label={t(`${table_name}.columns.calculation_start_date`, {
                      ns: "tables",
                      defaultValue: "Calculation Start Date",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 7: source */}
                <div className="grid grid-cols-1 gap-4">
                  <InputField
                    name="source"
                    label={t(`${table_name}.columns.source`, {
                      ns: "tables",
                      defaultValue: "Source",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before final submit */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, ["owner_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel/new-list navigation */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
